import { type MaintenanceWorkOrder } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/lib/api';
import {
  usePurgeMaintenanceWorkOrder,
  useRestoreTrashedMaintenanceWorkOrder,
} from '@/products/maintenance/features/work-orders/use-maintenance-work-orders';

type TrashRow = Pick<MaintenanceWorkOrder, 'id' | 'orderNumber'>;

function messageOf(error: unknown, fallback: string): string {
  return error instanceof ApiRequestError ? error.message : fallback;
}

export function MaintenanceWorkOrderTrashBar({
  orders,
  selectedIds,
  onClear,
}: {
  orders: readonly TrashRow[];
  selectedIds: ReadonlySet<string>;
  onClear: () => void;
}) {
  const selected = orders.filter((order) => selectedIds.has(order.id));
  const restore = useRestoreTrashedMaintenanceWorkOrder();
  const purge = usePurgeMaintenanceWorkOrder();
  const [mode, setMode] = useState<'restore' | 'purge' | null>(null);
  const [pending, setPending] = useState(false);

  if (selected.length === 0) {
    return null;
  }

  async function run(kind: 'restore' | 'purge') {
    if (selected.length === 0 || pending) {
      return;
    }
    setPending(true);
    const failures: string[] = [];
    for (const row of selected) {
      try {
        if (kind === 'restore') {
          await restore.mutateAsync(row.id);
        } else {
          await purge.mutateAsync(row.id);
        }
      } catch (error: unknown) {
        failures.push(`${row.orderNumber}: ${messageOf(error, 'fehlgeschlagen')}`);
      }
    }
    setPending(false);
    setMode(null);
    onClear();
    if (failures.length > 0) {
      toast.error(failures.join(' '));
      return;
    }
    toast.success(
      kind === 'restore'
        ? selected.length === 1
          ? 'Auftrag wiederhergestellt.'
          : `${selected.length} Aufträge wiederhergestellt.`
        : selected.length === 1
          ? 'Auftrag endgültig gelöscht.'
          : `${selected.length} Aufträge endgültig gelöscht.`,
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-muted-foreground">{selected.length} ausgewählt</p>
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          onClick={() => setMode('restore')}
        >
          Wiederherstellen
        </Button>
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          onClick={() => setMode('purge')}
        >
          Endgültig löschen
        </Button>
        <Button type="button" variant="ghost" className="min-h-11" onClick={onClear}>
          Auswahl aufheben
        </Button>
      </div>
      <AlertDialog
        open={mode != null}
        onOpenChange={(open) => {
          if (!open && !pending) {
            setMode(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {mode === 'purge'
                ? selected.length === 1
                  ? `Auftrag ${selected[0]?.orderNumber ?? ''} endgültig löschen?`
                  : `${selected.length} Aufträge endgültig löschen?`
                : selected.length === 1
                  ? `Auftrag ${selected[0]?.orderNumber ?? ''} wiederherstellen?`
                  : `${selected.length} Aufträge wiederherstellen?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {mode === 'purge'
                ? selected.length === 1
                  ? 'Der Auftrag und alle Dateien werden unwiderruflich gelöscht.'
                  : 'Die Aufträge und alle Dateien werden unwiderruflich gelöscht.'
                : 'Die Aufträge erscheinen wieder in Planung oder Archiv und in der Liste des Monteurs.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={pending || mode == null}
              onClick={(event) => {
                event.preventDefault();
                if (mode) {
                  void run(mode);
                }
              }}
            >
              {mode === 'purge' ? 'Endgültig löschen' : 'Wiederherstellen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
